import React, { memo, useMemo, useState, useCallback, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { Copy, Check, ArrowRight, Lightbulb, AlertTriangle, Info, AlertOctagon } from 'lucide-react'
import { cn } from '../../lib/utils'
// GitHub Dark theme for code block syntax highlighting (same as MarkdownViewer)
import 'highlight.js/styles/github-dark.css'

// Extend default sanitization schema to whitelist details/summary tags
// and allow lazy loading on images
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'details', 'summary'],
  attributes: {
    ...defaultSchema.attributes,
    img: [...(defaultSchema.attributes?.img || []), 'loading'],
    a: [...(defaultSchema.attributes?.a || []), 'dataAction'],
  },
}

interface RichMarkdownProps {
  content: string
  className?: string
  appName?: string // For resolving relative image URLs via file API
  basePath?: string // Current file path for relative path resolution
}

/**
 * Recursively extract plain text from React children.
 * Walks the children tree to collect strings, avoiding copying
 * HTML fragments like <span class="hljs-keyword">.
 */
function extractText(children: ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (children == null || typeof children === 'boolean') return ''
  if (Array.isArray(children)) return children.map(extractText).join('')
  if (typeof children === 'object' && 'props' in children) {
    return extractText(children.props.children)
  }
  return ''
}

const calloutConfig: Record<string, { icon: React.ElementType; bg: string; border: string; title: string; iconColor: string }> = {
  tip: { icon: Lightbulb, bg: 'bg-green-500/10', border: 'border-green-500/30', title: 'Tip', iconColor: 'text-green-500' },
  warning: { icon: AlertTriangle, bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', title: 'Warning', iconColor: 'text-yellow-500' },
  note: { icon: Info, bg: 'bg-blue-500/10', border: 'border-blue-500/30', title: 'Note', iconColor: 'text-blue-500' },
  caution: { icon: AlertOctagon, bg: 'bg-red-500/10', border: 'border-red-500/30', title: 'Caution', iconColor: 'text-red-500' },
}

/**
 * Recursively strip the [!TYPE] marker from React children.
 * Walks the tree to find and remove the marker text regardless of nesting depth.
 */
function stripCalloutMarker(children: ReactNode, type: string): ReactNode {
  const markerRe = new RegExp(`^\\s*\\[!${type.toUpperCase()}\\]\\s*`)
  let stripped = false

  function walk(node: ReactNode): ReactNode {
    if (stripped) return node
    if (typeof node === 'string') {
      if (markerRe.test(node)) {
        stripped = true
        const result = node.replace(markerRe, '')
        return result || null
      }
      return node
    }
    if (Array.isArray(node)) {
      return node.map(walk).filter(n => n != null)
    }
    if (node && typeof node === 'object' && 'props' in node) {
      const newChildren = walk((node as React.ReactElement).props.children)
      if (stripped) {
        return React.cloneElement(node as React.ReactElement, {}, newChildren)
      }
    }
    return node
  }

  return walk(children)
}

function CalloutBox({ type, children }: { type: string; children: ReactNode }) {
  const config = calloutConfig[type.toLowerCase()]
  if (!config) {
    return <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground">{children}</blockquote>
  }
  const Icon = config.icon
  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 my-4 not-prose`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${config.iconColor}`} />
        <span className="text-sm font-semibold">{config.title}</span>
      </div>
      <div className="text-sm text-muted-foreground">{stripCalloutMarker(children, type)}</div>
    </div>
  )
}

/**
 * Code block wrapper with copy-to-clipboard button.
 * Extracts plain text recursively from children rather than using innerHTML.
 */
function CodeBlock({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    const text = extractText(children as ReactNode)
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: clipboard API may not be available in all contexts
    }
  }, [children])

  return (
    <div className="relative group">
      <pre {...props}>{children}</pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-zinc-700/80 hover:bg-zinc-600 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-400" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  )
}

/** Generate a GitHub-style heading anchor ID */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

/** Heading component that generates anchor IDs for deep linking */
function HeadingWithId({ level, children, ...props }: { level: number; children?: ReactNode } & Record<string, unknown>) {
  const text = extractText(children)
  const id = slugify(text)
  const Tag = `h${level}` as keyof JSX.IntrinsicElements
  return <Tag id={id} {...props}>{children}</Tag>
}

function RichMarkdownInner({ content, className, appName, basePath }: RichMarkdownProps) {
  // Memoize components object to avoid react-markdown re-parsing on every render
  const components = useMemo(
    () => ({
      pre: CodeBlock,
      h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <HeadingWithId level={1} {...props}>{children}</HeadingWithId>,
      h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <HeadingWithId level={2} {...props}>{children}</HeadingWithId>,
      h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <HeadingWithId level={3} {...props}>{children}</HeadingWithId>,
      img: ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
        let resolvedSrc = src || ''
        // Resolve relative image paths through the deployed-apps file API
        if (
          appName &&
          src &&
          !src.startsWith('http://') &&
          !src.startsWith('https://') &&
          !src.startsWith('data:')
        ) {
          const dir = basePath ? basePath.split('/').slice(0, -1).join('/') : ''
          const imagePath = src.startsWith('/')
            ? src
            : dir
              ? `${dir}/${src}`
              : src
          resolvedSrc = `/api/deployed-apps/${encodeURIComponent(appName)}/files/content?path=${encodeURIComponent(imagePath)}&raw=true`
        }
        return (
          <img
            src={resolvedSrc}
            alt={alt || ''}
            className="max-w-full rounded"
            loading="lazy"
            {...props}
          />
        )
      },
      blockquote: ({ children }: React.BlockquoteHTMLAttributes<HTMLQuoteElement>) => {
        const text = extractText(children as ReactNode)
        const match = text.match(/^\s*\[!(TIP|WARNING|NOTE|CAUTION)\]/i)
        if (match) {
          return <CalloutBox type={match[1]}>{children}</CalloutBox>
        }
        return (
          <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground">
            {children}
          </blockquote>
        )
      },
      a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { 'data-action'?: string; dataAction?: string }) => {
        const dataAction = props['data-action'] || props.dataAction
        if (dataAction && href) {
          return (
            <Link
              to={href}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 text-sm font-medium no-underline"
            >
              {children}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )
        }
        if (href?.startsWith('http')) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          )
        }
        return (
          <a href={href} {...props}>
            {children}
          </a>
        )
      },
    }),
    [appName, basePath]
  )

  return (
    <div
      className={cn(
        'prose prose-sm dark:prose-invert max-w-none prose-pre:bg-[#0d1117] prose-pre:border prose-pre:border-border prose-code:before:content-none prose-code:after:content-none',
        className
      )}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], rehypeHighlight]}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  )
}

export const RichMarkdown = memo(RichMarkdownInner)
